import Skeleton from '@material-ui/lab/Skeleton/Skeleton';
import classNames from 'clsx';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Image from '../../components/Image/Image';
import { PKR_AVATAR_COUNT, PKR_LOADERS } from '../../constants';
import useGetCurrentClub from '../../hooks/useGetCurrentClub';
import { useHomeGamesWidget } from '../../model';
import { ClubPlayerStatus, UserClubRole } from '../../types/common';
import { getAvatarColor } from '../../utils/getAvatarColor';

import styles from './ClubMemberAvatars.scss';

interface Props {
  clubId: number;
  avatarClassName?: string;
  className?: string;
}

const ClubMemberAvatars: React.FC<Props> = ({ avatarClassName, clubId, className }) => {
  const { widgetDispatch, widgetSelect } = useHomeGamesWidget();
  const { nbMembers, roleForUser } = useGetCurrentClub(`${clubId}`);
  const loadingState = useSelector(widgetSelect.loading);
  const playersByClubId = useSelector(widgetSelect.playersByClubId);
  const data = playersByClubId[clubId] ?? [];
  const loading = loadingState[PKR_LOADERS.playersByClubId(clubId)] || !playersByClubId[clubId];
  const remaining =
    data.length === PKR_AVATAR_COUNT ? Math.abs(Number(nbMembers) - data.length) : 0;
  const isSuspended = roleForUser === UserClubRole.Suspended;
  const countArray = useMemo(() => Array.from(Array(PKR_AVATAR_COUNT).keys()), []);

  useEffect(() => {
    if (!isSuspended) {
      widgetDispatch.fetchClubAvatars({
        clubId,
        statuses: [ClubPlayerStatus.APPROVED],
        avatarIncluded: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  return (
    <div className={classNames(styles.container, className)}>
      {loading && !isSuspended ? (
        <div className={styles.loader}>
          {countArray.map((_, index) => (
            <Skeleton
              style={{ zIndex: PKR_AVATAR_COUNT - index }}
              key={`skeleton-${index}`}
              variant="circle"
            />
          ))}
        </div>
      ) : (
        <div className={styles.avatarContainer}>
          {!isSuspended &&
            data.length > 0 &&
            data.map(({ avatar, userId }, index) => (
              <div
                className={classNames(styles.avatar, avatarClassName)}
                key={userId}
                style={{
                  marginLeft: index === 0 ? 0 : -10,
                  zIndex: data.length - index,
                  backgroundColor: avatar ? 'none' : getAvatarColor(userId)
                }}
              >
                {avatar ? <Image imageByteArray={avatar} /> : `${userId}`.slice(0, 1)}
              </div>
            ))}
          {!isSuspended && remaining > 0 && <div className={styles.remaining}>+{remaining}</div>}
        </div>
      )}
    </div>
  );
};

export default ClubMemberAvatars;
